import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ThemeProvider as MuiThemeProvider, CssBaseline, Box, useMediaQuery } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { CircularProgress, Box as MuiBox } from '@mui/material';
// Add these imports for date picker localization
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';

const LazyFallback = () => (
  <MuiBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </MuiBox>
);

// Context & Theme
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { createAppTheme } from './theme';

// Components
import Navbar from './components/common/Navbar';
import RequireAuth from './components/auth/RequireAuth';
import PageTransition from './components/animations/PageTransition';
import { NotificationProvider } from './context/NotificationContext';
import { HelmetProvider } from 'react-helmet-async';
import PageTitle from './components/common/PageTitle';
import MaintenanceChecker from './components/common/MaintenanceChecker';
import MobileBottomNav from './components/common/MobileBottomNav';

// Pages - eagerly loaded (small, needed immediately)
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import MaintenancePage from './pages/MaintenancePage';

// Pages - lazy loaded
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const EmailPage = lazy(() => import('./pages/EmailPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ReportDetailPage = lazy(() => import('./pages/ReportDetailPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EmployeeDetailPage = lazy(() => import('./pages/EmployeeDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const WorkHoursPage = lazy(() => import('./pages/WorkHoursPage'));
const Invoices = lazy(() => import('./pages/Invoices'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const RecurringEventsPage = lazy(() => import('./pages/RecurringEventsPage'));
const TimeOffPage = lazy(() => import('./pages/TimeOffPage'));

// Changelog
import ChangelogModal from './components/changelog/ChangelogModal';
import useVersionCheck from './hooks/useVersionCheck';
import { usePageTracking } from './hooks/usePageTracking';
import { APP_VERSION } from './constants';

// Feedback
import FeedbackButton from './components/feedback/FeedbackButton';

/**
 * SetupRedirect component - redirects to setup page if setup is required
 */
function SetupRedirect({ children }) {
  const { setupRequired, loading } = useAuth();
  const location = useLocation();

  // Don't redirect if we're already on the setup page
  if (location.pathname === '/setup') {
    return children;
  }

  // Wait for loading to complete
  if (loading) {
    return children;
  }

  // Redirect to setup if required
  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }

  return children;
}

/**
 * AnimatedRoutes component handles route transitions with Framer Motion
 */
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <SetupRedirect>
      <AnimatePresence mode="wait">
        <React.Fragment key={location.pathname}>
          <PageTitle />
          <Suspense fallback={<LazyFallback />}>
          <Routes location={location} key={location.pathname}>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />

          {/* Protected routes */}
          <Route path="/calendar" element={
            <RequireAuth>
              <CalendarPage />
            </RequireAuth>
          } />
          <Route path="/recurring-events" element={
            <RequireAuth>
              <RecurringEventsPage />
            </RequireAuth>
          } />
          <Route path="/emails" element={
            <RequireAuth>
              <EmailPage />
            </RequireAuth>
          } />
          <Route path="/clients" element={
            <RequireAuth>
              <ClientsPage />
            </RequireAuth>
          } />
          <Route path="/clients/:clientId" element={
            <RequireAuth>
              <ClientDetailPage />
            </RequireAuth>
          } />
          <Route path="/work-hours" element={
            <RequireAuth>
              <WorkHoursPage />
            </RequireAuth>
          } />
          <Route path="/time-off" element={
            <RequireAuth>
              <TimeOffPage />
            </RequireAuth>
          } />
          <Route path="/admin" element={
            <RequireAuth adminOnly>
              <AdminPanel />
            </RequireAuth>
          } />
          <Route path="/reports/:reportId" element={
            <RequireAuth>
              <ReportDetailPage />
            </RequireAuth>
          } />
          <Route path="/dashboard" element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          } />
          <Route path="/employees/:id" element={
            <RequireAuth officeOnly>
              <EmployeeDetailPage />
            </RequireAuth>
          } />
          <Route path="/invoices" element={
            <RequireAuth officeOnly>
              <Invoices />
            </RequireAuth>
          } />
          {/* 404 fallback */}

          <Route path="/profile" element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          } />
          <Route path="/changelog" element={
            <RequireAuth>
              <ChangelogPage />
            </RequireAuth>
          } />
          <Route path="*" element={
            <PageTransition>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 200px)'
              }}>
                <h1>404 - Seite nicht gefunden</h1>
                <h2>Hier konnten wir nichts finden. :/</h2>
              </div>
            </PageTransition>
          } />
        </Routes>
          </Suspense>
      </React.Fragment>
    </AnimatePresence>
    </SetupRedirect>
  );
}

/**
 * ThemedAppContent component - wraps app with dynamic MUI theme based on user preference
 */
function ThemedAppContent() {
  const { effectiveMode } = useTheme();
  const { primary_color, secondary_color } = useBranding();
  const theme = useMemo(
    () => createAppTheme(effectiveMode, primary_color, secondary_color),
    [effectiveMode, primary_color, secondary_color]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </MuiThemeProvider>
  );
}

/**
 * AppContent component - handles version checking and changelog modal
 */
function AppContent() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const { hasNewVersion, markVersionSeen } = useVersionCheck();
  const isMobile = useMediaQuery('(max-width:899px)');

  // Track page views automatically
  usePageTracking();

  // Auto-show changelog modal on first login after version update
  useEffect(() => {
    const shownKey = `changelog_shown_${APP_VERSION}`;
    const hasShown = localStorage.getItem(shownKey);

    if (currentUser && hasNewVersion && !hasShown) {
      setShowChangelogModal(true);
      setAutoOpened(true);
      // Mark as shown so we don't show again for this version
      localStorage.setItem(shownKey, 'true');
    }
  }, [currentUser, hasNewVersion]);

  const handleVersionClick = () => {
    navigate('/changelog');
  };

  const handleCloseModal = async () => {
    if (autoOpened) {
      await markVersionSeen();
    }
    setShowChangelogModal(false);
    setAutoOpened(false);
  };

  return (
    <MaintenanceChecker>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <Navbar />
        <Box sx={{
          flex: 1,
          // Add bottom padding on mobile for the bottom nav
          pb: isMobile && currentUser ? '80px' : 0
        }}>
          <AnimatedRoutes />
        </Box>
        {/* Mobile Bottom Navigation */}
        {isMobile && <MobileBottomNav />}
        {/* Version Number - Clickable (hidden on mobile, shown in drawer instead) */}
        {!isMobile && (
          <Box
            onClick={handleVersionClick}
            sx={{
              position: 'fixed',
              bottom: 8,
              right: 10,
              fontSize: '8px',
              color: '#666',
              fontFamily: 'monospace',
              zIndex: 1000,
              userSelect: 'none',
              cursor: 'pointer',
              pointerEvents: 'auto',
              padding: '4px 6px',
              borderRadius: '4px',
              transition: 'all 0.2s',
              '&:hover': {
                color: '#333',
                backgroundColor: 'rgba(0,0,0,0.05)',
                textDecoration: 'underline'
              }
            }}
            title="Changelog anzeigen"
          >
            v{APP_VERSION}
          </Box>
        )}
      </Box>
      {/* Changelog Modal */}
      <ChangelogModal
        open={showChangelogModal}
        onClose={handleCloseModal}
        autoOpened={autoOpened}
      />
      {/* Feedback Button */}
      <FeedbackButton />
    </MaintenanceChecker>
  );
}

/**
 * Main App component
 */
function App() {
  return (
    <HelmetProvider>
    <BrandingProvider>
    <AuthProvider>
      <AnalyticsProvider>
        <ThemeProvider>
          <NotificationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}
              locale={de}
              adapterLocale={de}
              localeText={{
                cancelButtonLabel: 'Abbrechen',
                clearButtonLabel: 'Löschen',
                okButtonLabel: 'OK',
                todayButtonLabel: 'Heute',
              }}
              dateFormats={{
                monthAndYear: 'MMMM yyyy',
                fullDate: 'EEEE, dd. MMMM yyyy',
                keyboardDate: 'dd.MM.yyyy',
                shortDate: 'dd.MM.yy',
              }}
              // Ensure weeks start on Monday
              defaultCalendarMonth={new Date()}
              firstDayOfWeek={1}
            >
              <ThemedAppContent />
            </LocalizationProvider>
          </NotificationProvider>
        </ThemeProvider>
      </AnalyticsProvider>
    </AuthProvider>
    </BrandingProvider>
    </HelmetProvider>
  );
}

export default App;